import { Button } from 'tio-ui/components/buttons';
import Chip from 'tio-reimagined/components/chip';
import { concat, hash } from '@ember/helper';
import { dropTask } from 'ember-concurrency';
import EditPopover from './edit-popover.gts';
import { formatCamelCase } from '../../utils/format';
import { Input, Select, Textarea } from 'tio-ui/components/forms';
import { not, or } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { Popover } from 'tio-ui/components/popover';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import Table from 'tio-ui/components/table';
import TermVerified from '../profile/term-verified.gts';
import TioErrorMessages from '../tio/error-messages.gts';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type CompanyModel from '../../models/company';
import type EmployeeModel from '../../models/employee';
import type Intl from 'ember-intl/services/intl';
import type Owner from '@ember/owner';
import type PlanModel from '../../models/plan';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import { PhPencil, PhX, PhCheck } from 'ember-phosphor-icons';
import { fn } from '@ember/helper';

export interface UpdateEmployeeOverrideTableSignature {
  Args: {
    employee: EmployeeModel;
  };
  Element: HTMLDivElement;
}

export default class UpdateEmployeeOverrideTable extends Component<UpdateEmployeeOverrideTableSignature> {
  get cpkFieldName() {
    return 'contributionPlanKey';
  }

  get contributionPlanKey() {
    return this.args.employee.employeeAddition?.processed?.merged?.[this.cpkFieldName] || '';
  }

  get isContributionPlanKeyOverride() {
    if (!Array.isArray(this.args.employee.employeeAddition?.overrides)) {
      return false;
    }
    return this.args.employee.employeeAddition.overrides.some(
      ({ field }) => field === this.cpkFieldName
    );
  }

  get contributionAmountFieldName() {
    return 'contributionAmount';
  }

  get contributionAmount() {
    return (
      this.args.employee.employeeAddition?.processed?.merged?.[this.contributionAmountFieldName] ??
      ''
    );
  }

  get isContributionAmountOverride() {
    if (!Array.isArray(this.args.employee.employeeAddition?.overrides)) {
      return false;
    }
    return this.args.employee.employeeAddition.overrides.some(
      ({ field }) => field === this.contributionAmountFieldName
    );
  }

  <template>
    <Table class="!min-w-0">
      <:tbody as |options|>
        <options.tr>
          <options.td class={{options.cellClass}}>
            {{t "common.employees.fields.custom_contribution_amount"}}
          </options.td>
          <options.td class={{options.cellClass}}>
            {{String this.contributionAmount}}
          </options.td>
          <options.td class={{options.cellClass}}>
            <div class="flex justify-end">
              {{#if this.isContributionAmountOverride}}
                <div class="flex items-center gap-4">
                  <Chip @size="sm" @intent="default">{{t "common.override"}}</Chip>
                  <RevertOverridePopover
                    @fieldName={{this.contributionAmountFieldName}}
                    @employee={{@employee}}
                  />
                </div>
              {{/if}}
              <EditContributionAmount
                @value={{String this.contributionAmount}}
                @employee={{@employee}}
                @isOverride={{this.isContributionAmountOverride}}
              />
            </div>
          </options.td>
        </options.tr>
        <options.tr>
          <options.td class={{options.cellClass}}>
            {{t "common.employees.fields.contribution_plan_key"}}
          </options.td>
          <options.td class={{options.cellClass}}>
            {{String this.contributionPlanKey}}
          </options.td>
          <options.td class={{options.cellClass}}>
            <div class="flex justify-end">
              {{#if this.isContributionPlanKeyOverride}}
                <div class="flex items-center gap-4">
                  <Chip @size="sm" @intent="default">{{t "common.override"}}</Chip>
                  <RevertOverridePopover @fieldName={{this.cpkFieldName}} @employee={{@employee}} />
                </div>
              {{/if}}
              <EditContributionPlanKey
                @employee={{@employee}}
                @value={{String this.contributionPlanKey}}
              />
            </div>
          </options.td>
        </options.tr>
        {{#each-in @employee.employeeAddition.editableMergedFields as |key value|}}
          <UpdateOverrideRow
            @value={{value}}
            @fieldName={{key}}
            @employee={{@employee}}
            @cellClass={{options.cellClass}}
          />
        {{/each-in}}
        <options.tr>
          <options.td class={{options.cellClass}}>
            <TermVerified @employee={{@employee}} />
          </options.td>
        </options.tr>
      </:tbody>
    </Table>
  </template>
}

interface UpdateOverrideRowSignature {
  Args: {
    value: unknown;
    fieldName: string;
    employee: EmployeeModel;
    cellClass: string;
  };
  Element: HTMLDivElement;
}

class UpdateOverrideRow extends Component<UpdateOverrideRowSignature> {
  get isOverride() {
    const { overrides } = this.args.employee.employeeAddition;

    // Adding this check to fix an issue with bad data coming from the API
    if (!Array.isArray(overrides)) {
      return false;
    }

    return !!overrides.find(({ field }) => field === this.args.fieldName);
  }

  get overrideCellClass() {
    return this.args.cellClass + ' flex justify-end';
  }

  <template>
    <tr>
      <td class={{@cellClass}}>{{formatCamelCase @fieldName}}</td>
      <td class={{@cellClass}}>{{String @value}}</td>
      <td class={{this.overrideCellClass}}>
        {{#if this.isOverride}}
          <div class="flex items-center gap-4">
            <Chip @size="sm" @intent="default">{{t "common.override"}}</Chip>
            <RevertOverridePopover @fieldName={{@fieldName}} @employee={{@employee}} />
          </div>
        {{/if}}
        <EditFieldPopover @fieldName={{@fieldName}} @value={{@value}} @employee={{@employee}} />
      </td>
    </tr>
  </template>
}

interface EditFieldPopoverSignature {
  Args: {
    fieldName: string;
    value: unknown;
    employee: EmployeeModel;
  };
  Element: HTMLDivElement;
}

class EditFieldPopover extends Component<EditFieldPopoverSignature> {
  @tracked isOpen = false;
  @tracked editableValue!: unknown;
  @tracked reasonForEdit = '';
  @tracked submitError?: string;
  @tracked didSubmit = false;

  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare intl: Intl;

  constructor(owner: Owner, args: EditFieldPopoverSignature['Args']) {
    super(owner, args);
    this.syncEditableValue();
  }

  get isValid() {
    // TODO: Add type validations
    return !!this.reasonForEdit && !!this.editableValue && this.editableValue !== this.args.value;
  }

  syncEditableValue() {
    this.editableValue = this.args.value;
    this.reasonForEdit = '';
  }

  toggleOpen = () => {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.submitError = '';
      this.syncEditableValue();
    }
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  setEditableValue = (value: unknown) => {
    this.editableValue = value;
  };

  cancel = () => {
    this.submitError = '';
    this.didSubmit = false;
    this.toggleOpen();
  };

  async updateOverride(fieldName: string, value: unknown) {
    await this.store
      .adapterFor('employee')
      // @ts-expect-error - Legacy adapter method, will be migrated
      .updateOverrides(
        this.args.employee.id,
        { field: fieldName, value },
        { changelog: this.reasonForEdit }
      );
    await this.args.employee.employeeAddition.reload();
    const successMessage = this.intl.t('common.employees.fields.update_success', {
      field: formatCamelCase(fieldName),
    });
    this.notifications.add(successMessage, {
      appearance: 'success',
    });
  }

  updateTask = dropTask(async () => {
    this.submitError = '';
    this.didSubmit = true;
    try {
      await this.updateOverride(this.args.fieldName, this.editableValue);
      this.toggleOpen();
      this.didSubmit = false;
    } catch (e) {
      console.error(e);
      this.submitError = this.intl.t('common.employees.fields.update_error', {
        field: formatCamelCase(this.args.fieldName),
      });
    }
  });

  <template>
    <Popover @isOpen={{this.isOpen}} @onOpenChange={{this.toggleOpen}} as |p|>
      <Button
        @appearance="minimal"
        @intent="primary"
        title={{t "common.edit"}}
        {{p.trigger}}
        {{p.anchor}}
        data-test-update-override
      >
        <PhPencil @size="16" />
      </Button>
      <p.Content @backdrop="faded" @size="xl">
        <form class="flex flex-col gap-4 p-4">
          <h2 class="text-xl font-semibold">{{t "common.edit"}}
            {{formatCamelCase @fieldName}}</h2>
          <span class="text-gray-500 text-sm">
            {{t "common.employees.fields.update_override_disclaimer"}}
          </span>
          <Input
            data-test-update-override-input
            disabled={{this.updateTask.isRunning}}
            @value={{String this.editableValue}}
            @label={{formatCamelCase @fieldName}}
            @onChange={{this.setEditableValue}}
          />
          <Textarea
            data-test-reason-for-edit
            disabled={{this.updateTask.isRunning}}
            @value={{this.reasonForEdit}}
            @onInput={{this.updateReasonForEdit}}
            @label={{concat (t "common.comment") "*"}}
            @classes={{hash base="text-xs"}}
          />
          <div class="flex gap-2 justify-end">
            <Button
              data-test-update-override-cancel
              class="w-auto"
              @intent="danger"
              {{on "click" this.cancel}}
              disabled={{this.updateTask.isRunning}}
              title={{t "common.cancel"}}
            >
              <PhX @size="16" />
            </Button>
            <Button
              data-test-update-override-save
              class="w-auto"
              @intent="primary"
              {{on "click" this.updateTask.perform}}
              @isRunning={{this.updateTask.isRunning}}
              disabled={{not this.isValid}}
              title={{t "common.save"}}
            >
              <PhCheck @size="16" />
            </Button>
          </div>
          <TioErrorMessages
            @showErrors={{this.didSubmit}}
            @error={{this.submitError}}
            class="text-xs"
          />
        </form>
      </p.Content>
    </Popover>
  </template>
}

interface RevertOverridePopoverSignature {
  Args: {
    fieldName: string;
    employee: EmployeeModel;
  };
  Element: HTMLDivElement;
}

class RevertOverridePopover extends Component<RevertOverridePopoverSignature> {
  @tracked error?: string;

  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare intl: Intl;

  revertTask = dropTask(async (reasonForEdit: string) => {
    this.error = undefined;
    await this.store
      .adapterFor('employee')
      // @ts-expect-error - Legacy adapter method, will be migrated
      .updateOverrides(
        this.args.employee.id,
        { field: this.args.fieldName, value: null },
        { changelog: reasonForEdit }
      );
    await this.args.employee.reload({ include: 'employee-addition,plan' });
    const successMessage = this.intl.t('common.employees.fields.revert_success', {
      field: formatCamelCase(this.args.fieldName),
    });
    this.notifications.add(successMessage, {
      appearance: 'success',
    });
  });

  handleSubmit = async (reasonForEdit: string) => {
    try {
      await this.revertTask.perform(reasonForEdit);
    } catch (e) {
      console.error(e);
      this.error = e instanceof Error ? e.message : String(e);
    }
  };

  <template>
    <EditPopover
      @title={{concat (t "common.revert") " " (formatCamelCase @fieldName)}}
      @icon="revert"
      @size="lg"
      @isRunning={{this.revertTask.isRunning}}
      @error={{this.error}}
      @submitLabel={{t "common.revert"}}
      @onSubmit={{this.handleSubmit}}
      data-test-update-override-revert
    >
      <:description>
        <span class="text-gray-500 text-sm">
          {{t "common.employees.fields.revert_disclaimer"}}
        </span>
      </:description>
    </EditPopover>
  </template>
}

interface EditContributionAmountSignature {
  Args: {
    value: string;
    employee: EmployeeModel;
    isOverride: boolean;
  };
  Element: HTMLDivElement;
}

class EditContributionAmount extends Component<EditContributionAmountSignature> {
  @tracked isOpen = false;
  @tracked editableValue!: string;
  @tracked reasonForEdit = '';
  @tracked submitError?: string;
  @tracked didSubmit = false;

  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare intl: Intl;

  constructor(owner: Owner, args: EditContributionAmountSignature['Args']) {
    super(owner, args);
    this.syncEditableValue();
  }

  syncEditableValue() {
    this.editableValue = this.args.value;
    this.reasonForEdit = '';
  }

  get isValid() {
    return !!this.reasonForEdit && !!this.editableValue && this.editableValue !== this.args.value;
  }

  toggleOpen = () => {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.submitError = '';
      this.syncEditableValue();
    }
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  setEditableValue = (value: string) => {
    this.editableValue = value;
  };

  cancel = () => {
    this.submitError = '';
    this.didSubmit = false;
    this.toggleOpen();
  };

  async updateContributionAmount() {
    await this.store
      .adapterFor('employee')
      // @ts-expect-error - Legacy adapter method, will be migrated
      .updateOverrides(
        this.args.employee.id,
        { field: 'contributionAmount', value: this.editableValue },
        { changelog: this.reasonForEdit }
      );
    await this.args.employee.reload({ include: 'employee-addition' });
    const successMessage = this.intl.t('common.employees.fields.update_success', {
      field: this.intl.t('common.employees.fields.contribution_amount'),
    });
    this.notifications.add(successMessage, {
      appearance: 'success',
    });
  }

  updateTask = dropTask(async () => {
    this.submitError = '';
    this.didSubmit = true;
    try {
      await this.updateContributionAmount();
      this.toggleOpen();
      this.didSubmit = false;
    } catch (e) {
      console.error(e);
      this.submitError = this.intl.t('common.employees.fields.update_error', {
        field: this.intl.t('common.employees.fields.contribution_amount'),
      });
    }
  });

  <template>
    <Popover @isOpen={{this.isOpen}} @onOpenChange={{this.toggleOpen}} as |p|>
      <Button
        @appearance="minimal"
        @intent="primary"
        title={{t "common.edit"}}
        {{p.trigger}}
        {{p.anchor}}
        data-test-update-override
      >
        <PhPencil @size="16" />
      </Button>
      <p.Content @backdrop="faded" @size="xl">
        <form class="flex flex-col gap-4 p-4">
          <h2 class="text-xl font-semibold">
            {{#if @isOverride}}
              {{t "common.edit"}}
            {{else}}
              {{t "common.add"}}
            {{/if}}
            {{t "common.employees.fields.custom_contribution_amount"}}</h2>
          <span class="text-gray-500 text-sm">
            {{t "common.employees.fields.contribution_amount_disclaimer"}}
          </span>
          <Input
            data-test-update-override-input
            disabled={{this.updateTask.isRunning}}
            @value={{this.editableValue}}
            @label={{t "common.employees.fields.contribution_amount"}}
            @description={{t "common.employees.fields.contribution_amount_input_description"}}
            @onChange={{this.setEditableValue}}
          />
          <Textarea
            data-test-reason-for-edit
            disabled={{this.updateTask.isRunning}}
            @value={{this.reasonForEdit}}
            @onInput={{this.updateReasonForEdit}}
            @label={{concat (t "common.comment") "*"}}
            @classes={{hash base="text-xs"}}
          />
          <div class="flex gap-2 justify-end">
            <Button
              data-test-update-override-cancel
              class="w-auto"
              @intent="danger"
              {{on "click" this.cancel}}
              disabled={{this.updateTask.isRunning}}
              title={{t "common.cancel"}}
            >
              <PhX @size="16" />
            </Button>
            <Button
              data-test-update-override-save
              class="w-auto"
              @intent="primary"
              {{on "click" this.updateTask.perform}}
              @isRunning={{this.updateTask.isRunning}}
              disabled={{not this.isValid}}
              title={{t "common.save"}}
            >
              <PhCheck @size="16" />
            </Button>
          </div>
          <TioErrorMessages
            @showErrors={{this.didSubmit}}
            @error={{this.submitError}}
            class="text-xs"
          />
        </form>
      </p.Content>
    </Popover>
  </template>
}

interface EditContributionPlanKeySignature {
  Args: {
    employee: EmployeeModel;
    value: string;
  };
  Element: HTMLDivElement;
}

class EditContributionPlanKey extends Component<EditContributionPlanKeySignature> {
  @tracked isOpen = false;
  @tracked selectedKey!: string;
  @tracked reasonForEdit = '';
  @tracked submitError?: string;
  @tracked didSubmit = false;

  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare intl: Intl;

  constructor(owner: Owner, args: EditContributionPlanKeySignature['Args']) {
    super(owner, args);
    this.syncSelectedKey();
  }

  syncSelectedKey() {
    this.selectedKey = this.args.value;
    this.reasonForEdit = '';
  }

  get isValid() {
    return !!this.reasonForEdit && !!this.selectedKey && this.selectedKey !== this.args.value;
  }

  toggleOpen = () => {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.submitError = '';
      this.syncSelectedKey();
    }
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  setSelectedKey = (value: string) => {
    this.selectedKey = value;
  };

  cancel = () => {
    this.submitError = '';
    this.didSubmit = false;
    this.toggleOpen();
  };

  async updateContributionPlanKey() {
    await this.store
      .adapterFor('employee')
      // @ts-expect-error - Legacy adapter method, will be migrated
      .updateOverrides(
        this.args.employee.id,
        { field: 'contributionPlanKey', value: this.selectedKey[0] },
        { changelog: this.reasonForEdit }
      );
    await this.args.employee.reload({ include: 'employee-addition,plan' });
    const successMessage = this.intl.t('common.employees.fields.update_success', {
      field: this.intl.t('common.employees.fields.contribution_plan_key'),
    });
    this.notifications.add(successMessage, {
      appearance: 'success',
    });
  }

  updateTask = dropTask(async () => {
    this.submitError = '';
    this.didSubmit = true;
    try {
      await this.updateContributionPlanKey();
      this.toggleOpen();
      this.didSubmit = false;
    } catch (e) {
      console.error(e);
      this.submitError = this.intl.t('common.employees.fields.update_error', {
        field: this.intl.t('common.employees.fields.contribution_plan_key'),
      });
    }
  });

  get planOptions(): { key: string; label: string; name: string }[] {
    return (
      this.plansData.value?.map((plan: PlanModel) => ({
        key: plan.companyKey,
        label: plan.companyKey,
        name: plan.name,
      })) || []
    );
  }

  plansData = trackedFunction(this, async () => {
    const companyId = this.args.employee?.company?.id;
    if (!companyId) {
      return;
    }
    const companyWithPlans = await this.store.findRecord<CompanyModel>('company', companyId, {
      include: 'plans',
      reload: true,
    });
    return companyWithPlans.plans;
  });

  <template>
    <Popover @isOpen={{this.isOpen}} @onOpenChange={{this.toggleOpen}} as |p|>
      <Button
        @appearance="minimal"
        @intent="primary"
        title={{t "common.edit"}}
        {{p.trigger}}
        {{p.anchor}}
        data-test-update-override
      >
        <PhPencil @size="16" />
      </Button>
      <p.Content @backdrop="faded" @size="xl">
        <form class="flex flex-col gap-4 p-4">
          <h2 class="text-xl font-semibold">{{t "common.edit"}}
            {{t "common.employees.fields.contribution_plan_key"}}
          </h2>
          <Select
            data-test-update-contribution-key-select
            @isDisabled={{or this.plansData.isLoading this.updateTask.isRunning}}
            @selectedKey={{this.selectedKey}}
            @label={{t "common.employees.fields.contribution_plan_key"}}
            @onAction={{fn (mut this.selectedKey)}}
            @items={{this.planOptions}}
          >
            <:item as |option|>
              <option.Item @key={{option.item.key}} @description={{option.item.name}}>
                {{option.item.key}}
              </option.Item>
            </:item>
          </Select>
          <Textarea
            data-test-reason-for-edit
            disabled={{this.updateTask.isRunning}}
            @value={{this.reasonForEdit}}
            @onInput={{this.updateReasonForEdit}}
            @label={{concat (t "common.comment") "*"}}
            @classes={{hash base="text-xs"}}
          />
          <div class="flex gap-2 justify-end">
            <Button
              data-test-update-override-cancel
              class="w-auto"
              @intent="danger"
              {{on "click" this.cancel}}
              disabled={{this.updateTask.isRunning}}
              title={{t "common.cancel"}}
            >
              <PhX @size="16" />
            </Button>
            <Button
              data-test-update-override-save
              class="w-auto"
              @intent="primary"
              {{on "click" this.updateTask.perform}}
              @isRunning={{this.updateTask.isRunning}}
              disabled={{not this.isValid}}
              title={{t "common.save"}}
            >
              <PhCheck @size="16" />
            </Button>
          </div>
          <TioErrorMessages
            @showErrors={{this.didSubmit}}
            @error={{this.submitError}}
            class="text-xs"
          />
        </form>
      </p.Content>
    </Popover>
  </template>
}
